import type React from 'react';
import Badge from '@/components/UI/Badge';
import { trimDecimals } from '@einhaus/meteoric-helpers';
import type {
    PortfolioBacktest,
    PortfolioBacktestForwardEvaluation,
    PortfolioBacktestForwardSegmentStats
} from '@einhaus/project314-types';
import { WALK_FORWARD_DISCLAIMER } from './portfolioBacktestColumnConfig';
import { portfolioEvaluationWindowWarning } from '../PortfolioBacktestStats/portfolioWalkForwardBoundary';

const VERDICT_BADGE_VARIANTS = {
    passed: 'success',
    failed: 'danger',
    inconclusive: 'warning'
} as const;

const VERDICT_LABELS = {
    passed: 'Criteria met',
    failed: 'Criteria failed',
    inconclusive: 'Inconclusive'
} as const;

const formatSegment = (segment: PortfolioBacktestForwardSegmentStats): string => {
    if (segment.tradeCount === 0) return 'no trades';

    const averageReturn = segment.avgProfitPercent !== null ? `${trimDecimals(segment.avgProfitPercent, 2)}%/trade` : 'no return data';
    const winRate = segment.winRatePercent !== null ? `, ${trimDecimals(segment.winRatePercent, 1)}% win rate` : '';

    return `${averageReturn} over ${segment.tradeCount.toLocaleString()} trades${winRate}`;
};

/** Retains the historical numerical result while making development/validation overlap explicit. */
const buildWalkForwardTooltip = (
    evaluation: PortfolioBacktestForwardEvaluation,
    warning: ReturnType<typeof portfolioEvaluationWindowWarning>
): string => {
    const captureRatio = evaluation.captureRatioPercent !== null ? `${trimDecimals(evaluation.captureRatioPercent, 1)}%` : 'not measurable';

    const lines = [
        ...(warning ? [`${warning.label}: ${warning.reason}`, ''] : []),
        `Historical criteria result: ${VERDICT_LABELS[evaluation.verdict.status]}`,
        `Saved evaluation window: ${evaluation.trainingEndDateTime} through ${evaluation.walkForwardEndDateTime}`,
        '',
        `Signal stream (decides the criteria result): ${formatSegment(evaluation.stream.walkForward)}`,
        `Portfolio (shown in the column): ${formatSegment(evaluation.portfolio.walkForward)}`,
        `Capture ratio (portfolio vs stream, before saved split): ${captureRatio}`
    ];

    if (evaluation.verdict.reasons.length > 0) {
        lines.push('', ...evaluation.verdict.reasons.map((reason) => `• ${reason}`));
    }

    lines.push('', WALK_FORWARD_DISCLAIMER);

    return lines.join('\n');
};

interface PortfolioBacktestWalkForwardCellProps extends Pick<
    PortfolioBacktest,
    'componentTrainingEndDateTime' | 'componentValidationEndDateTime' | 'componentWindowsSource'
> {
    forwardEvaluation: PortfolioBacktestForwardEvaluation | null | undefined;
}

const PortfolioBacktestWalkForwardCell: React.FC<PortfolioBacktestWalkForwardCellProps> = ({
    forwardEvaluation,
    componentTrainingEndDateTime,
    componentValidationEndDateTime,
    componentWindowsSource
}) => {
    if (!forwardEvaluation) {
        return (
            <span className="text-zinc-500" title={WALK_FORWARD_DISCLAIMER}>
                -
            </span>
        );
    }

    const walkForward = forwardEvaluation.portfolio.walkForward;
    if (!walkForward) {
        return (
            <span className="text-zinc-500" title={WALK_FORWARD_DISCLAIMER}>
                -
            </span>
        );
    }

    const { avgProfitPercent: walkForwardReturn, tradeCount } = walkForward;

    const returnColorClass =
        walkForwardReturn === null || walkForwardReturn === 0
            ? 'text-zinc-400'
            : walkForwardReturn > 0
              ? 'text-profit-green !text-[#29a740]'
              : 'text-red-500';

    const warning = portfolioEvaluationWindowWarning({
        trainingEndDateTime: forwardEvaluation.trainingEndDateTime,
        componentTrainingEndDateTime,
        componentValidationEndDateTime,
        componentWindowsSource
    });
    const tooltip = buildWalkForwardTooltip(forwardEvaluation, warning);

    return (
        <div className="flex flex-col items-start gap-1" title={tooltip}>
            <Badge variant={warning ? 'warning' : VERDICT_BADGE_VARIANTS[forwardEvaluation.verdict.status]}>
                {warning?.label ?? VERDICT_LABELS[forwardEvaluation.verdict.status]}
            </Badge>
            {tradeCount === 0 ? (
                <span className="text-xs text-zinc-500">no trades after saved split</span>
            ) : (
                <>
                    <span className={`text-xs ${returnColorClass}`}>
                        {walkForwardReturn === null ? 'no return data' : `${trimDecimals(walkForwardReturn, 2)}%/trade`}
                    </span>
                    <span className="text-xs text-zinc-500">{tradeCount.toLocaleString()} trades after saved split</span>
                </>
            )}
        </div>
    );
};

export default PortfolioBacktestWalkForwardCell;
